import React, { useEffect, useRef, useState } from 'react';
import style from './style.module.css';
import boardToast, { TOAST_TYPE } from '../utils/boardToast';
import { hasOnlySpecialCharacters } from '../utils/general';
import { Button } from 'react-bootstrap';
import { selectLoader, titleLimit } from 'utils/Helpers';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import APIServices from 'services/APIServices';

const urlRegex = /(https?:\/\/[^\s]+)/g;

export default function EditBigBox({ onClose, handleSubmit, title, onChange = () => {}, isEditor = false }) {
  const [value, setValue] = useState(title || '');
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef(null);
  const reactQuillRef = useRef(null);

  // Focus editor/input on mount
  useEffect(() => {
    if (isEditor && reactQuillRef.current) {
      const quill = reactQuillRef.current.getEditor();

      // focus editor
      quill.focus();

      // move cursor to end
      const length = quill.getLength();
      quill.setSelection(length, length);
    }

    if (!isEditor && inputRef.current) {
      inputRef.current.focus();

      // move cursor to end for normal input too
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditor]);

  const handleValidate = (value) => {
    const plainText = value.replace(/<[^>]*>/g, '').trim();

    if (!plainText || plainText.length < 2) {
      boardToast('Min two characters are required', TOAST_TYPE.ERROR);
      setError(true);
      return;
    }

    if (plainText.length > titleLimit) {
      boardToast('Title cannot exceed 80 characters', TOAST_TYPE.ERROR);
      setError(true);
      return;
    }

    if (hasOnlySpecialCharacters(plainText)) {
      boardToast('The input contains only special characters. Please enter valid text.', TOAST_TYPE.ERROR);
      setError(true);
      return;
    }

    handleSubmit(plainText);
  };

  // ==================== LINK PREVIEW ====================
  const handleLinkPreview = async (url, index) => {
    try {
      setIsLoading(true);
      const response = await APIServices.post('/link/preview', { url });

      if (response?.title) {
        const quill = reactQuillRef.current.getEditor();

        // Remove raw URL
        quill.deleteText(index, url.length);

        // Insert hyperlink title
        quill.insertText(index, response.title, { link: url });
      }
    } catch (err) {
      console.error('Error fetching link preview:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== QUILL CHANGE ====================
  const handleQuillChange = (content, delta, source, editor) => {
    const plainText = editor.getText().trim();

    // enforce char limit
    if (plainText.length > titleLimit) {
      const truncated = plainText.substring(0, titleLimit);
      const quill = reactQuillRef.current.getEditor();
      quill.deleteText(0, quill.getLength());
      quill.insertText(0, truncated);
      setValue(truncated);
      onChange(truncated);
      return;
    }

    setValue(content);
    onChange(content);
    setError(false);

    // detect URLs
    if (source === 'user') {
      const matches = [...plainText.matchAll(urlRegex)];

      if (matches.length > 0) {
        const lastMatch = matches[matches.length - 1];
        const url = lastMatch[0];
        const index = lastMatch.index;

        handleLinkPreview(url, index);
      }
    }
  };

  return (
    <div
      className={`${style.input_wrapper} ${style.title_quill_wrapper}`}
      style={error ? { borderColor: '#D11A1A' } : {}}>
      {/* ================= TITLE (ReactQuill) ================= */}
      {isEditor ? (
        <>
          <ReactQuill
            ref={reactQuillRef}
            value={value}
            onChange={handleQuillChange}
            className={`mb-0 ml-1 ${style.title_form_editor}`}
            placeholder="Enter Title"
            modules={{ toolbar: false }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                handleValidate(value);
              }
            }}
          />
          {isLoading && <div className={style.link_loader}>{selectLoader(20)}</div>}
        </>
      ) : (
        /* ================= NORMAL INPUT ================= */
         <ReactQuill
            ref={reactQuillRef}
            value={value}
            onChange={handleQuillChange}
            className={`mb-0 ml-1 ${style.title_form_editor}`}
            placeholder="Enter Title"
            modules={{ toolbar: false }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
              }
              if (e.key === 'Enter') {
                e.preventDefault();
                handleValidate(value);
              }
            }}
          />
      )}

      {/* ================= BUTTONS ================= */}
      <div className="d-flex gap-2 ml-2 wrapper-btn">
        <Button
          variant="success"
          onClick={() => handleValidate(value)}
          style={{ padding: '0px 12px', height: '28px', lineHeight: '28px' }}>
          Save
        </Button>

        <Button variant="link" onClick={onClose} style={{ padding: '0px 12px', height: '28px', lineHeight: '28px' }}>
          Cancel
        </Button>
      </div>

      {/* Optional loader */}
    </div>
  );
}
